import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NativeSelect, Spinner, Stack, Tooltip, Typography } from "@altimateai/lego";
import { IconInfoCircle } from "@tabler/icons-react";
import { DateRange } from "@/components/dateRange";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { parseSortDirection } from "@/components/tables/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useAppState } from "@/modules/app/useAppContext";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getAIServiceGraph, getAIServiceUsage } from "@/helpers/apis";
import { BackButton } from "@/components/button";
import { FilterApplicableFor } from "@/components/tags/types";
import { DatastoreTypeEnum } from "@/pages/datastores/types";
import { AIServiceDetailCostGraph } from "./AIServiceDetailCostGraph";
import { DEFAULT_SERVICE_COLOR, getServiceByType } from "./constants";
import { useUsageTableHeaders } from "./useUsageTableHeaders";
import { useUsageFilters } from "./useUsageFilters";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const AIServiceDetailPageContent: FC = () => {
  const { serviceType } = useParams<{ serviceType: string }>();
  const { currency, currencySymbol, datastores = [] } = useAppState();
  const { startDate, endDate, onUpdate } = useDateFromSearchParams();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { isInitialized } = useDatatableContext();
  const [aggregateBy, setAggregateBy] = useState("day");

  const { costFilter, costFilterMode, activeFilters, registerColumns } = useUsageFilters();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);
  const sortAttribute = searchParams.get("sortAttribute") || "start_time";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const instance = searchParams.get("instance") || undefined;

  const service = serviceType ? getServiceByType(serviceType) : undefined;
  const serviceName = service?.displayName ?? serviceType ?? "Unknown Service";
  const serviceColor = service?.color ?? DEFAULT_SERVICE_COLOR;
  const serviceDescription = service?.description;

  useBreadcrumb(serviceName, {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "AI Services", pathname: "/query/ai-services" },
    ],
  });

  const ALL_INSTANCES = "__all__";

  const instanceOptions = useMemo(
    () => [
      { label: "All Instances", value: ALL_INSTANCES },
      ...datastores
        .filter(d => d.type === DatastoreTypeEnum.SNOWFLAKE && d.instance_id != null)
        .map(d => ({ label: d.name, value: String(d.instance_id) })),
    ],
    [datastores]
  );

  const handleInstanceChange = useCallback(
    (value: string) =>
      updateSearchParamsByKey({ instance: value === ALL_INSTANCES ? undefined : value }),
    [updateSearchParamsByKey]
  );

  const dateParams = useMemo(
    () => ({
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
    }),
    [startDate, endDate]
  );

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["ai_service_detail_graph", serviceType, dateParams, aggregateBy, instance],
    queryFn: () =>
      getAIServiceGraph(serviceType!, {
        ...dateParams,
        aggregation_level: aggregateBy,
        ...(instance ? { instance_id: instance } : {}),
      }),
    enabled: isInitialized && Boolean(serviceType),
  });

  const { data: usageData, isFetching: isUsageFetching } = useQuery({
    queryKey: [
      "ai_service_detail_usage",
      serviceType,
      dateParams,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      activeFilters,
      costFilter,
      costFilterMode,
      instance,
    ],
    queryFn: () =>
      getAIServiceUsage(serviceType!, {
        ...dateParams,
        page: page + 1,
        size: pageSize,
        sort_by: sortAttribute,
        sort_order: sortOrder,
        ...(instance ? { instance_id: instance } : {}),
        ...activeFilters,
        ...(costFilter ? { cost_filter: costFilter, cost_filter_mode: costFilterMode } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled: isInitialized && Boolean(serviceType),
  });

  useEffect(() => {
    registerColumns(usageData?.columns);
  }, [usageData?.columns, registerColumns]);

  const handlePageChange = useCallback(
    (newPage: number) => updateSearchParamsByKey({ page: String(newPage) }),
    [updateSearchParamsByKey]
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => updateSearchParamsByKey({ pageSize: String(newSize), page: "0" }),
    [updateSearchParamsByKey]
  );

  const tableHeaders = useUsageTableHeaders({
    usageData,
    serviceType,
    dateParams,
    sortAttribute,
    sortOrder,
    currency,
    currencySymbol,
    costFilterMode,
  });

  return (
    <Stack direction="column" className="al-bg-white al-p-4 al-rounded-lg">
      <Stack align="center" justify="between" className="al-w-full">
        <Stack align="center" gap="md">
          <BackButton defaultUrl="/query/ai-services" />
          <Typography variant="page-heading">{serviceName}</Typography>
          {serviceDescription && (
            <Tooltip title={serviceDescription}>
              <span className="al-text-light-gray al-cursor-help">
                <IconInfoCircle size={18} />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack align="center" gap="md">
          {instanceOptions.length > 1 && (
            <NativeSelect
              size="sm"
              variant="ghost"
              value={instance ?? ALL_INSTANCES}
              onChange={handleInstanceChange}
              options={instanceOptions}
              label="Instance:"
              showPlaceholder
              buttonProps={{ "aria-label": "Filter by instance" }}
            />
          )}
          <DateRange
            variant="inline"
            startDate={startDate}
            endDate={endDate}
            onDateRangeSelect={onUpdate}
          />
        </Stack>
      </Stack>

      <AIServiceDetailCostGraph
        graphData={graphData}
        isLoading={isGraphLoading}
        aggregateBy={aggregateBy}
        onAggregateChange={setAggregateBy}
        serviceColor={serviceColor}
        serviceType={serviceType ?? ""}
        dateParams={dateParams}
        instanceId={instance}
      />

      <Stack direction="column" className="al-mt-4">
        <Stack align="center" justify="between" className="al-mb-2">
          <Typography variant="section-heading">Usage Details</Typography>
          <div id="usage-filter-portal" />
        </Stack>
        {tableHeaders.length > 0 ? (
          <DataTable
            header={tableHeaders}
            data={{ items: usageData?.data ?? [] }}
            idKey={(item: Record<string, unknown>) =>
              String(item.query_id || item.service_id || item.username || JSON.stringify(item))
            }
            isLoading={isUsageFetching}
            applicableFor={FilterApplicableFor.AI_SERVICE_USAGE}
            portals={{ filter: "usage-filter-portal" }}
            defaultSort={{ sortParamValue: "start_time", order: "desc" }}
            clearFiltersKeysToPreserve={["start_date", "end_date", "instance"]}
          />
        ) : isUsageFetching ? (
          <Stack justify="center" align="center" gap="sm" className="al-w-full al-py-6">
            <Spinner size="md" />
            Loading...
          </Stack>
        ) : null}
        <Paginate
          itemCount={usageData?.pagination.total ?? 0}
          numPages={usageData?.pagination.pages ?? 0}
          page={page}
          pageSize={pageSize}
          onPageClick={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLoading={isUsageFetching}
          showPageSize
        />
      </Stack>
    </Stack>
  );
};
